import { TipoCampoBadge } from "@/components/common/TipoCampoBadge";
import { GOOGLE_API_KEY, GOOGLE_MAP_ID } from "@/constants/endpoints";
import { EstablishmentItem } from "@/services/establishments.service";
import { AppColors } from "@/styles/colors";
import { APIProvider, AdvancedMarker, Map, Polygon } from "@vis.gl/react-google-maps";
import { Box, CloseButton, Dialog, Flex, Portal, Text } from "@chakra-ui/react";
import { Dispatch, SetStateAction } from "react";
import { LightMode } from "../ui/color-mode";
import { CenterLock, DEFAULT_ZOOM, MIN_ZOOM } from "./map/map-utils";

interface Props {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  establishment: EstablishmentItem | null;
  showPerimeter?: boolean;
}

export const VerEstablecimientoMapModal = ({ open, setOpen, establishment, showPerimeter = false }: Props) => {
  if (!establishment) return null;

  const { nombre, tipoCampo, id, cantidadLotes, sumaHALotes, latitud, longitud, lotes } = establishment;
  const hasLocation = latitud != null && longitud != null;
  const center = hasLocation ? { lat: latitud, lng: longitud } : { lat: 0, lng: 0 };

  return (
    <Dialog.Root
      placement="center"
      lazyMount
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <LightMode>
            <Dialog.Content maxWidth={{ base: "95vw", md: "720px", "2xl": "860px" }} width="100%">
              <Dialog.Header paddingY="14px" paddingX="24px">
                <Dialog.Title fontWeight={600} color={AppColors.GRAY_TEXT} fontSize="18px">
                  Ver establecimiento
                </Dialog.Title>
              </Dialog.Header>

              <Dialog.Body paddingX="0" paddingY="0">
                <Flex flexDir="column" paddingX="24px" paddingTop="10px" paddingBottom="4px" gap="6px">
                  <Text fontWeight={600} fontSize="15px" color={AppColors.GRAY_TEXT}>
                    {nombre}
                  </Text>
                  <Flex gap="24px" alignItems="center" flexWrap="wrap" paddingBottom="6px">
                    <TipoCampoBadge tipo={tipoCampo} />
                    <Text fontSize="13px" color={AppColors.GRAY_TEXT_ALT}>
                      ID Agrobit: {id}
                    </Text>
                    <Text fontSize="13px" color={AppColors.GRAY_TEXT_ALT}>
                      {cantidadLotes} lotes
                    </Text>
                    <Text fontSize="13px" color={AppColors.GRAY_TEXT_ALT}>
                      {sumaHALotes ?? "-"} ha
                    </Text>
                    {hasLocation && (
                      <Text fontSize="13px" color={AppColors.GRAY_TEXT_ALT}>
                        Coordenadas: {latitud.toFixed(4)}, {longitud.toFixed(4)}
                      </Text>
                    )}
                  </Flex>
                </Flex>

                <Box padding="20px">
                  <Box height={{ base: "260px", md: "320px", "2xl": "440px" }} width="100%" overflow="hidden" borderRadius="8px">
                    {hasLocation ? (
                      <APIProvider apiKey={GOOGLE_API_KEY}>
                        <Map
                          defaultCenter={center}
                          defaultZoom={DEFAULT_ZOOM}
                          minZoom={MIN_ZOOM}
                          disableDefaultUI
                          gestureHandling="greedy"
                          style={{ width: "100%", height: "100%" }}
                          mapId={GOOGLE_MAP_ID}
                        >
                          <CenterLock center={center} />
                          <AdvancedMarker position={center} />
                          {showPerimeter && lotes?.map((lote) => {
                            const path = lote.coordenadasPoligono?.puntos?.map((p) => ({
                              lat: p.latitud,
                              lng: p.longitud,
                            }));
                            if (!path?.length) return null;
                            return (
                              <Polygon
                                key={lote.id}
                                paths={path}
                                strokeColor={AppColors.LIGHT_GRAY_TEXT}
                                strokeOpacity={0.9}
                                strokeWeight={2}
                                fillColor={AppColors.LIGHT_GRAY_TEXT}
                                fillOpacity={0.25}
                              />
                            );
                          })}
                        </Map>
                      </APIProvider>
                    ) : (
                      <Flex
                        height="100%"
                        width="100%"
                        alignItems="center"
                        justifyContent="center"
                        borderRadius="8px"
                        backgroundColor={AppColors.BACKGROUND_SECONDARY}
                        border="1px solid"
                        borderColor={AppColors.LIGHT_GRAY_TEXT}
                      >
                        <Text fontSize="14px" color={AppColors.GRAY_TEXT_ALT}>
                          Ubicación no disponible
                        </Text>
                      </Flex>
                    )}
                  </Box>
                </Box>
              </Dialog.Body>

              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </LightMode>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};
